<script setup>
import { computed } from "vue";
import { cva } from "class-variance-authority";
const props = defineProps({
  connector: {
    type: Object,
    required: true
  },
  legacyApiUrl: {
    type: String,
    required: true
  },
  isStandalone: {
    type: Boolean,
    default: false
  },
  isSelected: {
    type: Boolean,
    default: false
  },
  isDisabled: {
    type: Boolean,
    default: false
  },
  customCssClass: {
    type: String,
    default: ""
  }
});
defineEmits(["selectConnector"]);
const cellStyles = computed(
  () => cva("w-full flex gap-2 425:gap-4 p-4 items-center", {
    variants: {
      isStandalone: {
        true: "border rounded-lg 425:rounded-2xl"
      },
      isDisabled: {
        true: "border-gray-disabled cursor-not-allowed",
        false: "cursor-pointer"
      },
      isSelected: {
        true: "bg-primary-light"
      },
      isSelectedStandalone: {
        true: "border-light-blue-primary"
      }
    }
  })({
    isStandalone: props.isStandalone,
    isDisabled: props.isDisabled,
    isSelected: props.isSelected && !props.isDisabled,
    isSelectedStandalone: props.isSelected && props.isStandalone && !props.isDisabled
  })
);
const iconStyles = computed(
  () => cva("size-8 425:size-11", {
    variants: {
      isDefault: {
        true: "text-gray-l-800"
      },
      isDisabled: {
        true: "text-gray-disabled opacity-20"
      },
      isSelected: {
        true: "text-badge-info"
      }
    }
  })({
    isDefault: !props.isSelected && !props.isDisabled,
    isSelected: props.isSelected && !props.isDisabled,
    isDisabled: props.isDisabled
  })
);
const connectorNameStyles = computed(
  () => cva("text-[9px] 425:text-[13px] font-medium leading-[10px] 425:leading-4", {
    variants: {
      isDefault: {
        true: "text-gray-l-500"
      },
      isDisabled: {
        true: "text-gray-disabled"
      },
      isSelected: {
        true: "text-light-blue-primary"
      }
    }
  })({
    isDefault: !props.isSelected && !props.isDisabled,
    isSelected: props.isSelected && !props.isDisabled,
    isDisabled: props.isDisabled
  })
);
const connectorPowerStyles = computed(
  () => cva(
    "text-[15px] 425:text-[17px] font-medium leading-[18px] 425:leading-5",
    {
      variants: {
        isDefault: {
          true: "text-gray-l-800"
        },
        isDisabled: {
          true: "text-gray-disabled"
        },
        isSelected: {
          true: "text-primary-info"
        }
      }
    }
  )({
    isDefault: !props.isSelected && !props.isDisabled,
    isSelected: props.isSelected && !props.isDisabled,
    isDisabled: props.isDisabled
  })
);
const chevronIconStyles = computed(
  () => cva("size-5 425:size-6 ml-auto", {
    variants: {
      isSelected: {
        true: !props.isDisabled && "text-icon-secondary-branding",
        false: !props.isDisabled && "text-icon-secondary-neutral"
      },
      isDisabled: {
        true: "text-gray-disabled"
      }
    }
  })({
    isSelected: props.isSelected,
    isDisabled: props.isDisabled
  })
);
const connectorIconURL = computed(() => {
  const { connectorType } = props.connector;
  const baseUrl = `${props.legacyApiUrl}/medias/v2/connector_types/icon/original`;
  return connectorType ? `${baseUrl}/${connectorType.id}/${connectorType.icon}` : `${baseUrl}/unknown/plug.svg`;
});
</script>

<template>
    <div
        :class="[cellStyles, !isStandalone && customCssClass]"
        @click="!isDisabled && $emit('selectConnector', connector)"
    >
        <img
            :src="connectorIconURL"
            :class="iconStyles"
            :alt="connector.operatorName ?? ''"
        />
        <div class="flex flex-col gap-0.5 items-start w-full">
            <span :class="connectorNameStyles">
                {{ connector.operatorName }}
            </span>
            <span :class="connectorPowerStyles">
                {{
                    $t("simulation.connector.max_power_unit", {
  power: connector.power
})
                }}
            </span>
        </div>
        <IconChevronRight :class="chevronIconStyles" />
    </div>
</template>
